import { useMemo } from 'react';
import type { PagerState } from '../../lib/pocket-pager';
import { analyzeFairness, anyFairnessRuleOn, FAIRNESS_VISIBLE_CAP } from '../../lib/fairness-check';
import { countFieldTime } from '../../lib/field-time-live';
import { REPORT_POSITION_ORDER } from '../../lib/field-time-report';
import { shortName } from '../plan/DefenseGrid';
import { InningStrip } from '../plan/InningStrip';

// Now | Next: the live game's right pane on a wide screen (unfolded iPhone Duo,
// iPad landscape, laptop). The left pane is the live game, unchanged; this side
// works out the next decision so that at the third out the coach confirms
// instead of calculating. A switcher picks the view:
//   Next        who's on the mound and how close to the limit, the bullpen,
//               next inning's planned defense and what changes, fairness notes
//   Pitches     the pitch tracker, always beside the diamond (players-pitch)
//   Field time  innings so far per player, with fairness notes
//
// Read-only: every change to the game still happens in the left pane and the
// inning flow. Suggestive voice: it informs, it never decides.

export type SideView = 'next' | 'pitches' | 'fieldtime';

type Entry = { id: string; position: string };

const SectionTitle = ({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) => (
  <div className="flex items-baseline justify-between px-1 mb-2 mt-5 first:mt-0">
    <h3 className="text-sm font-semibold text-ink-2">{children}</h3>
    {right && <span className="text-[13px] text-ink-2 tabular-nums">{right}</span>}
  </div>
);

const levelText = (level?: string) =>
  level === 'limit' ? 'text-danger' : level === 'warn' ? 'text-gold-ink' : 'text-accent-ink';

const Row = ({ left, right, rightClass = 'text-ink-2', last }: { left: React.ReactNode; right?: React.ReactNode; rightClass?: string; last?: boolean }) => (
  <div className={`flex items-baseline gap-3 px-4 py-2.5 ${last ? '' : 'border-b border-hairline'}`}>
    <span className="flex-1 min-w-0 truncate text-[13.5px] font-semibold text-ink">{left}</span>
    {right != null && <span className={`shrink-0 text-[12.5px] tabular-nums ${rightClass}`}>{right}</span>}
  </div>
);

export const SidePane = ({
  view, onView, isPlayersPitch, currentInning, totalInnings,
  pager, nextPlannedArm, plannedPitcherNextInning, bullpenAvailable, bullpenOnRest, bullpenUnavailable,
  lineup, inningLogs, planInnings, settings, rosterNames, removedPlayerIds, trackerNode,
}: {
  view: SideView;
  onView: (v: SideView) => void;
  isPlayersPitch: boolean;
  currentInning: number;
  totalInnings: number;
  /** The same lines the Lock Screen shows (buildPagerState). */
  pager: PagerState;
  nextPlannedArm: any | null;
  plannedPitcherNextInning: any | null;
  bullpenAvailable: any[];
  bullpenOnRest: { player: any; until: string }[];
  bullpenUnavailable: { player: any; reason: string }[];
  /** Live lineup in batting order, each with its current position. */
  lineup: any[];
  /** Innings already played, keyed by inning number (1-based). */
  inningLogs: Record<number, Entry[]>;
  /** The saved plan's innings, 0-based ("0" is inning 1). */
  planInnings?: Record<string, Entry[]>;
  settings: any;
  rosterNames: Record<string, string>;
  removedPlayerIds: string[];
  /** The pitch tracker, built by the engine so both panes share one in-progress at-bat. */
  trackerNode: React.ReactNode;
}) => {
  const views: { key: SideView; label: string }[] = [
    { key: 'next', label: 'Next' },
    ...(isPlayersPitch ? [{ key: 'pitches' as const, label: 'Pitches' }] : []),
    { key: 'fieldtime', label: 'Field time' },
  ];
  const nameOf = (id: string) => shortName(lineup.find((p: any) => p.id === id)?.name || rosterNames[id] || '');

  // Innings so far: every logged inning before this one, plus the one being played.
  const played: Entry[][] = useMemo(() => {
    const before = Object.keys(inningLogs).map(Number).filter(n => n < currentInning).sort((a, b) => a - b);
    return [...before.map(n => inningLogs[n] || []), lineup.map((p: any) => ({ id: p.id, position: p.position }))];
  }, [inningLogs, currentInning, lineup]);

  const findings = useMemo(() => {
    if (!anyFairnessRuleOn(settings)) return null;
    const logs: Record<number, Entry[]> = {};
    played.forEach((inn, i) => { logs[i + 1] = inn; });
    const current = played[played.length - 1] || [];
    return analyzeFairness(current, logs, settings, rosterNames, totalInnings, removedPlayerIds);
  }, [played, settings, rosterNames, totalInnings, removedPlayerIds]);

  // Next inning, as the plan has it (0-based plan: inning N + 1 sits at key N).
  const nextPlan = planInnings?.[String(currentInning)] || null;
  const nextInningNumber = currentInning + 1;
  const plannedAt = (pos: string) => nextPlan?.find(e => e.position === pos)?.id;
  const currentPos = (id: string) => lineup.find((p: any) => p.id === id)?.position;
  const positions = REPORT_POSITION_ORDER.filter(pos => plannedAt(pos));
  const sittingNext = (nextPlan || []).filter(e => e.position === 'BENCH').map(e => e.id);
  const changes = (nextPlan || []).filter(e => currentPos(e.id) && currentPos(e.id) !== e.position).length;

  const fairnessBlock = findings && (
    <>
      <SectionTitle>Fairness so far</SectionTitle>
      <div className="bg-surface rounded-card px-4 py-3 space-y-1.5">
        {findings.length === 0 ? (
          <p className="text-[13.5px] text-ink-2">Nothing your fairness rules flag.</p>
        ) : (
          <>
            {findings.slice(0, FAIRNESS_VISIBLE_CAP).map((f, i) => (
              <p key={`${f.rule}-${i}`} className="text-[13.5px] text-ink leading-snug">{f.message}</p>
            ))}
            {findings.length > FAIRNESS_VISIBLE_CAP && (
              <p className="text-[12.5px] text-ink-2">+{findings.length - FAIRNESS_VISIBLE_CAP} more</p>
            )}
          </>
        )}
      </div>
    </>
  );

  return (
    <>
      <div className="px-4 pt-3 pb-3 bg-surface border-b border-hairline">
        <div role="tablist" aria-label="Side view" className="flex p-1 rounded-control bg-raised">
          {views.map(v => (
            <button
              key={v.key}
              role="tab"
              aria-selected={view === v.key}
              onClick={() => onView(v.key)}
              className={`flex-1 h-[34px] rounded-[9px] text-[13.5px] font-semibold ${
                view === v.key ? 'bg-surface text-ink shadow-sm' : 'text-ink-2'
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {view === 'pitches' && isPlayersPitch ? (
        <div className="flex-1 overflow-y-auto">{trackerNode}</div>
      ) : (
        <div className="flex-1 overflow-y-auto bg-canvas px-4 pt-4 pb-6">
          {view === 'next' && (
            <>
              {isPlayersPitch && pager.pitcherName && (
                <>
                  <SectionTitle>On the mound</SectionTitle>
                  <div className="bg-surface rounded-card px-4 py-3">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-[17px] font-semibold text-ink truncate">{pager.pitcherName}</span>
                      <span className={`text-[17px] font-black tabular-nums ${levelText(pager.level)}`}>{pager.pitchLine}</span>
                    </div>
                    {pager.limitNote && <p className="text-[13px] text-ink-2 mt-1">{pager.limitNote}</p>}
                  </div>
                </>
              )}

              {isPlayersPitch && (
                <>
                  <SectionTitle right={`${bullpenAvailable.length} ready`}>Bullpen</SectionTitle>
                  <div className="bg-surface rounded-card">
                    {plannedPitcherNextInning && (
                      <Row left={`Inning ${nextInningNumber}: ${shortName(plannedPitcherNextInning.name)}`} right="Planned" rightClass="text-accent-ink font-semibold" />
                    )}
                    {bullpenAvailable.length === 0 && bullpenOnRest.length === 0 && bullpenUnavailable.length === 0 && (
                      <p className="px-4 py-3 text-[13.5px] text-ink-2">No other arms tagged as pitchers.</p>
                    )}
                    {bullpenAvailable.map((p: any, i: number) => (
                      <Row
                        key={p.id}
                        left={shortName(p.name)}
                        right={nextPlannedArm?.id === p.id ? 'Next up' : 'Ready'}
                        rightClass={nextPlannedArm?.id === p.id ? 'text-accent-ink font-semibold' : 'text-ink-2'}
                        last={i === bullpenAvailable.length - 1 && bullpenOnRest.length === 0 && bullpenUnavailable.length === 0}
                      />
                    ))}
                    {bullpenOnRest.map(({ player, until }, i) => (
                      <Row key={player.id} left={shortName(player.name)} right={`Rest until ${until}`} rightClass="text-gold-ink"
                        last={i === bullpenOnRest.length - 1 && bullpenUnavailable.length === 0} />
                    ))}
                    {bullpenUnavailable.map(({ player, reason }, i) => (
                      <Row key={player.id} left={shortName(player.name)} right={reason} last={i === bullpenUnavailable.length - 1} />
                    ))}
                  </div>
                </>
              )}

              <SectionTitle right={nextPlan ? (changes === 0 ? 'Same defense' : `${changes} ${changes === 1 ? 'change' : 'changes'}`) : undefined}>
                Inning {nextInningNumber} defense
              </SectionTitle>
              <div className="bg-surface rounded-card">
                {!nextPlan ? (
                  <p className="px-4 py-3 text-[13.5px] text-ink-2">
                    No plan for inning {nextInningNumber}. Auto Lineup builds one between innings.
                  </p>
                ) : (
                  <>
                    {positions.map((pos, i) => {
                      const id = plannedAt(pos)!;
                      const from = currentPos(id);
                      const moved = !!from && from !== pos;
                      return (
                        <div key={pos} className={`flex items-baseline gap-3 px-4 py-2 ${i === positions.length - 1 && sittingNext.length === 0 ? '' : 'border-b border-hairline'}`}>
                          <span className="w-[34px] shrink-0 text-[12.5px] font-bold text-ink-2 tabular-nums">{pos}</span>
                          <span className={`flex-1 min-w-0 truncate text-[13.5px] ${moved ? 'font-bold text-ink' : 'font-semibold text-ink'}`}>{nameOf(id)}</span>
                          <span className="shrink-0 text-[12.5px] text-ink-2">
                            {!from ? '' : moved ? `from ${from === 'BENCH' ? 'bench' : from}` : 'stays'}
                          </span>
                        </div>
                      );
                    })}
                    {sittingNext.length > 0 && (
                      <p className="px-4 py-2.5 text-[13px] text-ink-2">
                        Sitting: {sittingNext.map(nameOf).join(', ')}
                      </p>
                    )}
                  </>
                )}
              </div>

              {fairnessBlock}
            </>
          )}

          {view === 'fieldtime' && (
            <>
              {fairnessBlock}
              <SectionTitle right={`Through inning ${currentInning}`}>Innings so far</SectionTitle>
              <FieldTimeRows lineup={lineup} played={played} />
            </>
          )}
        </div>
      )}
    </>
  );
};

/** Innings so far, per player, most-benched first. */
const FieldTimeRows = ({ lineup, played }: { lineup: any[]; played: Entry[][] }) => {
  const counts = countFieldTime(played, lineup.map((p: any) => p.id));
  const rows = [...lineup].sort((a, b) => (counts.get(b.id)?.bench ?? 0) - (counts.get(a.id)?.bench ?? 0));
  return (
    <div className="bg-surface rounded-card px-3 py-2">
      {rows.map((p: any, idx: number) => {
        const c = counts.get(p.id);
        const positions = played.map(inn => inn.find(e => e.id === p.id)?.position ?? 'BENCH');
        return (
          <div key={p.id} className={`flex items-center gap-3 px-1 py-1.5 ${idx === rows.length - 1 ? '' : 'border-b border-hairline'}`}>
            <span className="w-[96px] shrink-0 truncate text-[13.5px] font-semibold text-ink">{shortName(p.name)}</span>
            <span className="flex-1 min-w-0 overflow-hidden"><InningStrip positions={positions} /></span>
            <span className="shrink-0 text-[12.5px] text-ink-2 tabular-nums">
              {[c?.bench ? `sits ${c.bench}` : '', c?.eh ? `EH ${c.eh}` : ''].filter(Boolean).join(', ') || 'plays all'}
            </span>
          </div>
        );
      })}
    </div>
  );
};
